import type { ReactNode } from 'react';
import type { CustomerRevenue } from '@metron/shared';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip.tsx';
import { Sparkbar } from './Sparkbar.tsx';

export const REVENUE_CAPTION = 'Invoiced, net of unapplied cash, approximate.';

const NO_FIGURE_REASON = {
  not_linked: 'No billing data linked',
  not_computed: 'Revenue not yet computed',
} as const;

const HEURISTIC_EXPLANATION =
  'Some of this revenue was attributed by matching Epicor customer IDs by prefix ' +
  '(the CustID family heuristic), not by Metron ID or Salesforce hierarchy. Treat it as less certain.';

const compactUsd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

const wholeUsd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

function WithTooltip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0}>{children}</span>
        </TooltipTrigger>
        <TooltipContent side='top' className='max-w-xs text-sm'>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function HeuristicMarker() {
  return (
    <WithTooltip label={HEURISTIC_EXPLANATION}>
      <span className='ml-1 text-xs text-muted-foreground underline decoration-dotted'>
        heuristic
      </span>
    </WithTooltip>
  );
}

interface RevenueAmountProps {
  /** Undefined means the customer is absent from the response: treated as not linked. */
  revenue: CustomerRevenue | undefined;
  pick: (revenue: CustomerRevenue) => number | null;
  compact?: boolean;
}

/**
 * One revenue figure, or an em dash with the reason when there is no figure.
 * Never renders $0 for a customer that is unlinked or not yet computed.
 */
export function RevenueAmount({ revenue, pick, compact = false }: RevenueAmountProps) {
  const state = revenue?.state ?? 'not_linked';
  const value = revenue ? pick(revenue) : null;
  if (state !== 'computed' || value === null) {
    const reason = state === 'computed' ? NO_FIGURE_REASON.not_linked : NO_FIGURE_REASON[state];
    return (
      <WithTooltip label={reason}>
        <span className='text-muted-foreground' aria-label={reason}>—</span>
      </WithTooltip>
    );
  }
  return (
    <span>
      {(compact ? compactUsd : wholeUsd).format(value)}
      {revenue?.weakestMethod === 'family' && <HeuristicMarker />}
    </span>
  );
}

const formatMonth = (month: string) =>
  new Date(`${month}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

/** Detail-page body: T12M, since-2024, monthly sparkbar and provenance. */
export function CustomerRevenueSummary({ revenue }: { revenue: CustomerRevenue | undefined }) {
  const monthly = revenue?.monthly ?? [];
  return (
    <div className='space-y-3'>
      <div className='grid grid-cols-2 gap-4'>
        <div>
          <p className='text-sm font-medium text-muted-foreground'>Trailing 12 months</p>
          <p className='text-lg font-semibold'>
            <RevenueAmount revenue={revenue} pick={(r) => r.trailing12m} />
          </p>
        </div>
        <div>
          <p className='text-sm font-medium text-muted-foreground'>Since Jan 2024</p>
          <p className='text-lg font-semibold'>
            <RevenueAmount revenue={revenue} pick={(r) => r.sinceJan2024} />
          </p>
        </div>
      </div>
      {monthly.length > 0 && (
        <div>
          {/* ponytail: Sparkbar draws no negative bars; a net-negative month (credit memos) shows as empty. */}
          <Sparkbar
            values={monthly.map((p) => Math.max(p.revenue, 0))}
            width={Math.max(monthly.length * 8, 96)}
            height={40}
            ariaLabel={`Monthly revenue, ${formatMonth(monthly[0].month)} to ${
              formatMonth(monthly[monthly.length - 1].month)
            }`}
          />
          <p className='text-xs text-muted-foreground'>
            {formatMonth(monthly[0].month)} – {formatMonth(monthly[monthly.length - 1].month)}
          </p>
        </div>
      )}
      {revenue && revenue.epicorRecordCount > 0 && (
        <p className='text-xs text-muted-foreground'>
          Attributed from {revenue.epicorRecordCount.toLocaleString()} Epicor billing{' '}
          {revenue.epicorRecordCount === 1 ? 'record' : 'records'}
          {revenue.computedAt && ` · as of ${new Date(revenue.computedAt).toLocaleString('en-US')}`}
        </p>
      )}
    </div>
  );
}
